import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Loading, Page, Section, Sheet, useFail, useToast } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { label, phone, when } from "../lib/format.ts";

const RULES: Record<string, string> = {
  similar_name_same_landline: "Similar name, same landline",
  similar_name_same_property: "Similar name, same property",
  shared_contact: "Shares a phone or email",
  same_mobile_different_name: "Same mobile, different first name",
  same_email_different_name: "Same email, different first name",
  same_contact_added_manually: "Added by hand with a number already on file",
};

function Who({ p }: { p: any }) {
  return (
    <div className="who">
      <b><Link to={`/people/${p.id}`}>{p.display_name}</Link></b>
      <div className="c">{(p.contacts ?? []).map((c: string) => { const [k, v] = c.split(/:(.+)/); return <div key={c}>{k === "email" ? v : phone(v.replace(" (shared)", "")) + (v.includes("shared") ? " (shared)" : "")}</div>; })}</div>
      <div className="c" style={{ marginTop: 6 }}>{[p.roles.map(label).join(", "), p.original_source, (p.systems ?? []).join(", ")].filter(Boolean).join(" · ")}</div>
      <div className="c">{p.deals} deal{p.deals === 1 ? "" : "s"} · {p.messages} message{p.messages === 1 ? "" : "s"} · added {when(p.created_at)}</div>
    </div>
  );
}

export function Duplicates() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["duplicates"], queryFn: () => api.get("/duplicates") });
  const [merging, setMerging] = useState<any>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["duplicates"] }); qc.invalidateQueries({ queryKey: ["people"] }); };
  const dismiss = async (id: string) => { try { await api.post(`/duplicates/${id}/dismiss`); toast("Kept apart"); refresh(); } catch (e) { fail(e); } };
  const undo = async (id: string) => { try { await api.post(`/merges/${id}/undo`); toast("Merge undone: both people are back"); refresh(); } catch (e) { fail(e); } };
  return (
    <Page title="Duplicates">
      <div className="banner"><Icon name="merge" size={18} /><span>The same mobile or email merges by itself on import. Weaker matches wait here for you or Zoe. Every merge can be undone.</span></div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : (
        <>
          <Section title="Possible duplicates" count={q.data.pairs.length}>
            {q.data.pairs.length === 0 ? <Empty>Nothing to check.</Empty> : (
              <div className="list">
                {q.data.pairs.map((p: any) => p.a && p.b && (
                  <div key={p.id} className="pair">
                    <Who p={p.a} /><Who p={p.b} />
                    <div className="why">
                      <span className="badge">{RULES[p.rule] ?? p.rule.replace(/_/g, " ")} · {Math.round(p.score * 100)}%</span>
                      <div className="actions">
                        <button className="btn ghost sm" onClick={() => dismiss(p.id)}>Keep apart</button>
                        <button className="btn sm" onClick={() => setMerging(p)}>Merge</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <Section title="Recent merges" count={q.data.recent.length}>
            {q.data.recent.length === 0 ? <Empty>None yet.</Empty> : (
              <div className="list">{q.data.recent.map((m: any) => (
                <div key={m.id} className="row">
                  <div className="main-col"><div className="title">{m.merged_name} → {m.kept_name}</div><div className="sub">{RULES[m.match_rule] ?? m.match_rule.replace(/_/g, " ")} · {m.merged_by_name} · {when(m.merged_at)}</div></div>
                  {m.undone_at ? <span className="badge">Undone</span> : <button className="btn ghost sm" onClick={() => undo(m.id)}><Icon name="undo" />Undo</button>}
                </div>
              ))}</div>
            )}
          </Section>
        </>
      )}
      {merging && (
        <Sheet title="Which record do you keep?" onClose={() => setMerging(null)}>
          <p className="muted" style={{ marginTop: 0 }}>Everything on the other one (numbers, emails, deals, messages, notes, tasks, documents) moves across. Nothing is thrown away, and an opt-out on either carries over.</p>
          {[merging.a, merging.b].map((keep: any) => {
            const other = keep === merging.a ? merging.b : merging.a;
            return (
              <button key={keep.id} className="row" onClick={async () => {
                try { await api.post("/people/merge", { keep_id: keep.id, merge_id: other.id, suggestion_id: merging.id }); toast(`Merged into ${keep.display_name}`); setMerging(null); refresh(); } catch (e) { fail(e); }
              }}>
                <div className="main-col"><div className="title">Keep {keep.display_name}</div><div className="sub">{keep.deals} deals, {keep.messages} messages · {keep.original_source ?? ""}</div></div>
                <Icon name="chevron" className="chev" />
              </button>
            );
          })}
        </Sheet>
      )}
    </Page>
  );
}
